import { useMemo, useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { MultiSelect } from '@/components/ui/multi-select';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import {
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  ChevronDown,
  ChevronRight,
  ChevronLeft,
  Users,
  Clock,
  Activity as ActivityIcon,
  AlertTriangle
} from 'lucide-react';
import {
  PipelineLoan,
  PipelineStatus,
  DateRange,
  DatePreset,
  STATUS_CONFIG,
  getBorrowerName,
  getLoanOfficerName
} from '../types';
import { format, parseISO, startOfMonth } from 'date-fns';
import { formatBusinessDateTimeShort } from '@/utils/dateHelpers';
import { buildTimeAxis, STATUS_LINE_CONFIG } from './pipelineLineModel';
import {
  flattenActivityEvents,
  rollupProductivity,
  rollupVolume,
  rollupVelocity,
  distinctLoans,
  computeStalledLoans,
  type ActivityEvent
} from './activityReportModel';

interface PipelineActivityReportProps {
  loans: PipelineLoan[];
  isLoading: boolean;
  dateRange?: DateRange;
  datePreset?: DatePreset;
  // Drill-down seed from the Line Report (a status-count cell click). A fresh
  // object per click re-applies even for the same status.
  seed?: { status: PipelineStatus } | null;
  // Called after the seed is applied so the parent can clear it (one-shot) —
  // otherwise reopening this tab would re-apply the stale filter on remount.
  onSeedConsumed?: () => void;
}

type SortField = 'employee' | 'role' | 'milestone' | 'loan' | 'borrower' | 'lo' | 'amount' | 'date';
type SortDir = 'asc' | 'desc';

const PAGE_SIZE = 100;

function formatCurrency(amount: number): string {
  if (amount >= 1000000) {
    return `$${(amount / 1000000).toFixed(1)}M`;
  }
  if (amount >= 1000) {
    return `$${(amount / 1000).toFixed(0)}K`;
  }
  return `$${amount.toFixed(0)}`;
}

function formatEventDate(value: string): string {
  return /^\d{4}-\d{2}-\d{2}$/.test(value)
    ? format(parseISO(`${value}T12:00:00`), 'MM/dd/yy')
    : formatBusinessDateTimeShort(value);
}

const ROLE_COLORS: Record<string, string> = {
  'Loan Officer': 'bg-blue-50 text-blue-700',
  'Loan Officer 2': 'bg-blue-50 text-blue-700',
  'LO Assistant': 'bg-sky-50 text-sky-700',
  Processor: 'bg-amber-50 text-amber-700',
  Underwriter: 'bg-purple-50 text-purple-700',
  Closer: 'bg-teal-50 text-teal-700',
  Operations: 'bg-slate-50 text-slate-700'
};

function CollapsiblePanel({
  title,
  subtitle,
  icon,
  open,
  onToggle,
  children
}: {
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <button type="button" className="flex w-full items-center justify-between" onClick={onToggle}>
          <CardTitle className="text-base flex items-center gap-2">
            {icon}
            {title}
            {subtitle && <span className="text-xs font-normal text-muted-foreground">{subtitle}</span>}
          </CardTitle>
          {open
            ? <ChevronDown className="h-4 w-4 text-muted-foreground" />
            : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
        </button>
      </CardHeader>
      {open && <CardContent className="pt-0">{children}</CardContent>}
    </Card>
  );
}

export function PipelineActivityReport({ loans, isLoading, dateRange, seed, onSeedConsumed }: PipelineActivityReportProps) {
  const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [page, setPage] = useState(0);

  const [showLeaderboard, setShowLeaderboard] = useState(true);
  const [showVolume, setShowVolume] = useState(true);
  const [showVelocity, setShowVelocity] = useState(false);
  const [showStale, setShowStale] = useState(false);

  // Concrete window (parent always supplies dateRange; guard for standalone
  // use). Memoized so downstream memos don't churn on the fallback Dates.
  const rangeFrom = useMemo(() => dateRange?.from ?? startOfMonth(new Date()), [dateRange?.from]);
  const rangeTo = useMemo(() => dateRange?.to ?? new Date(), [dateRange?.to]);

  const axis = useMemo(() => buildTimeAxis({ from: rangeFrom, to: rangeTo }), [rangeFrom, rangeTo]);

  // All in-range milestone actions (entity-scoped population + Pacific window).
  const events = useMemo(
    () => flattenActivityEvents(loans, { from: rangeFrom, to: rangeTo }),
    [loans, rangeFrom, rangeTo]
  );

  // Apply the Line Report drill-down seed to the Status filter (one-shot).
  useEffect(() => {
    if (seed?.status) {
      setSelectedStatuses([seed.status]);
      onSeedConsumed?.();
    }
  }, [seed, onSeedConsumed]);

  // Reset to the first page whenever the filters change.
  useEffect(() => {
    setPage(0);
  }, [selectedUsers, selectedRoles, selectedStatuses, search, rangeFrom, rangeTo]);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  // Filter options (with counts) derived from the in-range events.
  const employeeOptions = useMemo(() => {
    const map = new Map<string, number>();
    events.forEach(({ doneBy }) => {
      const name = doneBy || 'Unassigned';
      map.set(name, (map.get(name) || 0) + 1);
    });
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name, count]) => ({ label: `${name} (${count})`, value: name }));
  }, [events]);

  const roleOptions = useMemo(() => {
    const map = new Map<string, number>();
    events.forEach(({ role }) => {
      map.set(role, (map.get(role) || 0) + 1);
    });
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([role, count]) => ({ label: `${role} (${count})`, value: role }));
  }, [events]);

  const statusOptions = useMemo(() => {
    const map = new Map<PipelineStatus, number>();
    events.forEach(({ status }) => {
      map.set(status, (map.get(status) || 0) + 1);
    });
    // Ordered by canonical pipeline position.
    return STATUS_LINE_CONFIG.filter((c) => map.has(c.status)).map((c) => ({
      label: `${c.label} (${map.get(c.status)})`,
      value: c.status
    }));
  }, [events]);

  // In-tab filters drive the feed AND the event-derived panels.
  const filteredEvents = useMemo(() => {
    const q = search.trim().toLowerCase();
    return events.filter((e) => {
      const name = e.doneBy || 'Unassigned';
      if (selectedUsers.length > 0 && !selectedUsers.includes(name)) {
        return false;
      }
      if (selectedRoles.length > 0 && !selectedRoles.includes(e.role)) {
        return false;
      }
      if (selectedStatuses.length > 0 && !selectedStatuses.includes(e.status)) {
        return false;
      }
      if (q) {
        const hay = `${getBorrowerName(e.loan)} ${e.loan.trans_details_loan_number || ''}`.toLowerCase();
        if (!hay.includes(q)) {
          return false;
        }
      }
      return true;
    });
  }, [events, selectedUsers, selectedRoles, selectedStatuses, search]);

  const productivity = useMemo(() => rollupProductivity(filteredEvents), [filteredEvents]);
  const volume = useMemo(() => rollupVolume(filteredEvents, axis), [filteredEvents, axis]);
  const velocity = useMemo(() => rollupVelocity(distinctLoans(filteredEvents)), [filteredEvents]);
  // Stalled is an as-of-today snapshot (shared Loan Center calc): entity-filtered
  // population only, independent of the date range and the in-tab event filters.
  const stalled = useMemo(() => computeStalledLoans(loans), [loans]);

  const sortedEntries = useMemo(() => {
    return [...filteredEvents].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case 'employee':
          cmp = (a.doneBy || 'Unassigned').localeCompare(b.doneBy || 'Unassigned');
          break;
        case 'role':
          cmp = a.role.localeCompare(b.role);
          break;
        case 'milestone':
          cmp = (STATUS_CONFIG[a.status]?.label || '').localeCompare(STATUS_CONFIG[b.status]?.label || '');
          break;
        case 'loan':
          cmp = (a.loan.trans_details_loan_number || '').localeCompare(b.loan.trans_details_loan_number || '');
          break;
        case 'borrower':
          cmp = getBorrowerName(a.loan).localeCompare(getBorrowerName(b.loan));
          break;
        case 'lo':
          cmp = getLoanOfficerName(a.loan).localeCompare(getLoanOfficerName(b.loan));
          break;
        case 'amount':
          cmp = (a.loan.trans_details_loan_amt || 0) - (b.loan.trans_details_loan_amt || 0);
          break;
        case 'date':
          cmp = a.doneDate.localeCompare(b.doneDate);
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [filteredEvents, sortField, sortDir]);

  const pageCount = Math.max(1, Math.ceil(sortedEntries.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pagedEntries = sortedEntries.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  const maxVolume = Math.max(1, ...volume.points.map((p) => p.count));

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="ml-1 h-3 w-3 opacity-50" />;
    }
    return sortDir === 'asc'
      ? <ArrowUp className="ml-1 h-3 w-3" />
      : <ArrowDown className="ml-1 h-3 w-3" />;
  };

  const SortHeader = ({ field, children, align }: { field: SortField; children: React.ReactNode; align?: 'right' }) => (
    <th className={`py-2 px-3 font-medium ${align === 'right' ? 'text-right' : 'text-left'}`}>
      <button
        type="button"
        className="inline-flex items-center gap-0.5 hover:text-foreground text-muted-foreground transition-colors"
        onClick={() => handleSort(field)}
      >
        {children}
        <SortIcon field={field} />
      </button>
    </th>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12" role="status" aria-label="Loading team activity">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        <span className="sr-only">Loading team activity</span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm text-muted-foreground whitespace-nowrap">
          {filteredEvents.length} of {events.length} actions
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="w-[200px]">
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search borrower or loan #"
              className="h-9 text-sm"
            />
          </div>
          <div className="w-[180px]">
            <MultiSelect getSelectedLabel={(value) => value} options={roleOptions} selected={selectedRoles} onChange={setSelectedRoles} placeholder="All Roles" className="min-h-[36px] text-sm" />
          </div>
          <div className="w-[180px]">
            <MultiSelect getSelectedLabel={(value) => value} options={statusOptions} selected={selectedStatuses} onChange={setSelectedStatuses} placeholder="All Milestones" className="min-h-[36px] text-sm" />
          </div>
          <div className="w-[220px]">
            <MultiSelect getSelectedLabel={(value) => value} options={employeeOptions} selected={selectedUsers} onChange={setSelectedUsers} placeholder="All Employees" className="min-h-[36px] text-sm" />
          </div>
        </div>
      </div>

      {/* Productivity leaderboard */}
      <CollapsiblePanel
        title="Productivity"
        subtitle="who moved loans this period"
        icon={<Users className="h-4 w-4 text-muted-foreground" />}
        open={showLeaderboard}
        onToggle={() => setShowLeaderboard((v) => !v)}
      >
        {productivity.rows.length === 0 ? (
          <div className="text-sm text-muted-foreground py-2">No activity for the current filters.</div>
        ) : (
          <>
            <div className="mb-3 flex flex-wrap gap-1.5">
              {productivity.byRole.map((r) => (
                <span key={r.role} className={`inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium ${ROLE_COLORS[r.role] || ROLE_COLORS['Operations']}`}>
                  {r.role}
                  <span className="tabular-nums">{r.actions}</span>
                </span>
              ))}
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <caption className="sr-only">Employee productivity for milestone activity</caption>
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="text-left py-1.5 px-2 font-medium">Employee</th>
                    <th className="text-left py-1.5 px-2 font-medium">Role</th>
                    <th className="text-right py-1.5 px-2 font-medium">Actions</th>
                    <th className="text-right py-1.5 px-2 font-medium">Loans</th>
                    <th className="text-right py-1.5 px-2 font-medium">Volume</th>
                  </tr>
                </thead>
                <tbody>
                  {productivity.rows.map((r) => (
                    <tr key={r.key} className="border-b last:border-0">
                      <td className="py-1.5 px-2">{r.displayName}</td>
                      <td className="py-1.5 px-2">
                        <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${ROLE_COLORS[r.role] || ROLE_COLORS['Operations']}`}>
                          {r.role}
                        </span>
                      </td>
                      <td className="py-1.5 px-2 text-right tabular-nums">{r.actions}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums">{r.loans}</td>
                      <td className="py-1.5 px-2 text-right tabular-nums">{formatCurrency(r.volume)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </CollapsiblePanel>

      {/* Volume over time */}
      <CollapsiblePanel
        title="Activity volume"
        subtitle={axis.granularity === 'daily' ? 'actions per day' : 'actions per month'}
        icon={<ActivityIcon className="h-4 w-4 text-muted-foreground" />}
        open={showVolume}
        onToggle={() => setShowVolume((v) => !v)}
      >
        {volume.byStatus.length === 0 ? (
          <div className="text-sm text-muted-foreground py-2">No activity for the current filters.</div>
        ) : (
          <>
            <div className="h-[200px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={volume.points} margin={{ top: 5, right: 10, left: 0, bottom: 5 }} accessibilityLayer>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} interval="preserveStartEnd" />
                  <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} allowDecimals={false} width={30} domain={[0, maxVolume]} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'hsl(var(--card))',
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Bar dataKey="count" name="Actions" fill="hsl(var(--chart-1))" radius={[2, 2, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5">
              {volume.byStatus.map((s) => (
                <span key={s.status} className="inline-flex items-center gap-1 rounded border px-2 py-0.5 text-xs">
                  <span className="font-medium" style={{ color: s.color }}>{s.label}</span>
                  <span className="tabular-nums text-muted-foreground">{s.count}</span>
                </span>
              ))}
            </div>
          </>
        )}
      </CollapsiblePanel>

      {/* Time in stage / velocity */}
      <CollapsiblePanel
        title="Time in stage"
        subtitle="avg / median days between stages"
        icon={<Clock className="h-4 w-4 text-muted-foreground" />}
        open={showVelocity}
        onToggle={() => setShowVelocity((v) => !v)}
      >
        {velocity.length === 0 ? (
          <div className="text-sm text-muted-foreground py-2">Not enough stage history for the current filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <caption className="sr-only">Elapsed time between reached pipeline stages</caption>
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="text-left py-1.5 px-2 font-medium">Transition</th>
                  <th className="text-right py-1.5 px-2 font-medium">Median days</th>
                  <th className="text-right py-1.5 px-2 font-medium">Avg days</th>
                  <th className="text-right py-1.5 px-2 font-medium">Loans</th>
                </tr>
              </thead>
              <tbody>
                {velocity.map((v) => (
                  <tr key={`${v.fromStatus}>${v.toStatus}`} className="border-b last:border-0">
                    <td className="py-1.5 px-2">
                      {v.fromLabel} → {v.toLabel}
                      {!v.adjacent && (
                        <span className="ml-1 text-[10px] text-amber-600" title="Skips a stage">(skips a stage)</span>
                      )}
                    </td>
                    <td className="py-1.5 px-2 text-right tabular-nums">{v.medianDays}</td>
                    <td className="py-1.5 px-2 text-right tabular-nums">{v.avgDays}</td>
                    <td className="py-1.5 px-2 text-right tabular-nums">{v.n}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CollapsiblePanel>

      {/* Stalled (shared Loan Center calc — as of today) */}
      <CollapsiblePanel
        title="Stalled"
        subtitle="as of today · matches the Loan Center 'stalled' flag"
        icon={<AlertTriangle className="h-4 w-4 text-muted-foreground" />}
        open={showStale}
        onToggle={() => setShowStale((v) => !v)}
      >
        <div className="mb-3 flex items-center gap-2 text-sm">
          <span className="font-medium tabular-nums">{stalled.length}</span>
          <span className="text-muted-foreground">
            stalled active loan{stalled.length === 1 ? '' : 's'} — 7+ Pacific days since the milestone date (a newer conversation log clears it)
          </span>
        </div>
        {stalled.length === 0 ? (
          <div className="text-sm text-muted-foreground py-2">No stalled active loans.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <caption className="sr-only">Active loans stalled as of today</caption>
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="text-left py-1.5 px-2 font-medium">Loan #</th>
                  <th className="text-left py-1.5 px-2 font-medium">Borrower</th>
                  <th className="text-left py-1.5 px-2 font-medium">Stage</th>
                  <th className="text-left py-1.5 px-2 font-medium">Owner</th>
                  <th className="text-right py-1.5 px-2 font-medium">Days stale</th>
                </tr>
              </thead>
              <tbody>
                {stalled.map((s) => (
                  <tr key={s.loan.encompass_loan_id} className="border-b last:border-0">
                    <td className="py-1.5 px-2">{s.loan.trans_details_loan_number || '-'}</td>
                    <td className="py-1.5 px-2 max-w-[160px] truncate">{getBorrowerName(s.loan)}</td>
                    <td className="py-1.5 px-2">
                      <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${STATUS_CONFIG[s.stage]?.color || ''}`}>
                        {s.stageLabel}
                      </span>
                    </td>
                    <td className="py-1.5 px-2">
                      {s.ownerName}
                      <span className="ml-1 text-[10px] text-muted-foreground">({s.ownerRole})</span>
                    </td>
                    <td className="py-1.5 px-2 text-right tabular-nums font-medium">{s.daysStale}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </CollapsiblePanel>

      {/* Activity feed */}
      <Card>
        <CardContent className="pt-4">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <caption className="sr-only">Team milestone activity details</caption>
              <thead>
                <tr className="border-b">
                  <SortHeader field="employee">Employee</SortHeader>
                  <SortHeader field="role">Role</SortHeader>
                  <SortHeader field="milestone">Milestone</SortHeader>
                  <SortHeader field="loan">Loan #</SortHeader>
                  <SortHeader field="borrower">Borrower</SortHeader>
                  <SortHeader field="lo">LO</SortHeader>
                  <SortHeader field="amount" align="right">Amount</SortHeader>
                  <SortHeader field="date" align="right">Event date</SortHeader>
                </tr>
              </thead>
              <tbody>
                {pagedEntries.map((entry: ActivityEvent, idx) => {
                  const config = STATUS_CONFIG[entry.status];
                  const roleColor = ROLE_COLORS[entry.role] || ROLE_COLORS['Operations'];
                  return (
                    <tr
                      key={`${entry.loan.encompass_loan_id}-${entry.status}-${idx}`}
                      className="border-b hover:bg-muted/50"
                    >
                      <td className="py-2 px-3">{entry.doneBy || '—'}</td>
                      <td className="py-2 px-3">
                        <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[10px] font-medium ${roleColor}`}>
                          {entry.role}
                        </span>
                      </td>
                      <td className="py-2 px-3">
                        <span className={`inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium ${config.color}`}>
                          {config.label}
                        </span>
                      </td>
                      <td className="py-2 px-3">{entry.loan.trans_details_loan_number || '-'}</td>
                      <td className="py-2 px-3 max-w-[150px] truncate">{getBorrowerName(entry.loan)}</td>
                      <td className="py-2 px-3 max-w-[120px] truncate">{getLoanOfficerName(entry.loan)}</td>
                      <td className="py-2 px-3 text-right">
                        {entry.loan.trans_details_loan_amt
                          ? formatCurrency(entry.loan.trans_details_loan_amt)
                          : '-'}
                      </td>
                      <td className="py-2 px-3 text-right text-muted-foreground whitespace-nowrap">
                        {formatEventDate(entry.doneDate)}
                      </td>
                    </tr>
                  );
                })}
                {sortedEntries.length === 0 && (
                  <tr>
                    <td colSpan={8} className="text-center py-8 text-muted-foreground">
                      No activity found for the selected criteria.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {sortedEntries.length > PAGE_SIZE && (
            <div className="flex items-center justify-between pt-3 text-sm text-muted-foreground">
              <span>
                Showing {safePage * PAGE_SIZE + 1}–{Math.min((safePage + 1) * PAGE_SIZE, sortedEntries.length)} of {sortedEntries.length}
              </span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={safePage === 0}>
                  <ChevronLeft className="h-4 w-4" /> Prev
                </Button>
                <span className="tabular-nums">{safePage + 1} / {pageCount}</span>
                <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} disabled={safePage >= pageCount - 1}>
                  Next <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
